import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getCurrentUserId } from '@/utils/adminHelper';
import { Business, BusinessData } from '@/types/business';
import BusinessFormHeader from './BusinessFormHeader';
import BusinessDetailsFields from './BusinessDetailsFields';
import CategoryFields from './CategoryFields';
import ContactInfoFields from './ContactInfoFields';
import { BusinessFileUploader } from './BusinessFileUploader';
import FormActions from './FormActions';
import { useQueryClient } from '@tanstack/react-query';

interface BusinessFormProps {
  business: Business | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const BusinessForm: React.FC<BusinessFormProps> = ({ 
  business, 
  onClose, 
  onSuccess 
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<BusinessData>({
    business_name: '',
    company_name: '',
    description: '',
    logo_url: '',
    pitch_deck_url: '',
    website_preview_url: '',
    development_stage: 'development',
    industry_type: '',
    website: '',
    contact_email: '',
    target_segment: '',
    status: 'draft',
    gallery_images: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (business) {
      setFormData({
        business_name: business.business_name || '',
        company_name: business.company_name || '',
        description: business.description || '',
        logo_url: business.logo_url || '',
        pitch_deck_url: business.pitch_deck_url || '',
        website_preview_url: business.website_preview_url || '',
        development_stage: business.development_stage || 'development',
        industry_type: business.industry_type || '',
        website: business.website || '',
        contact_email: business.contact_email || '',
        target_segment: business.target_segment || '',
        status: business.status || 'draft',
        gallery_images: business.gallery_images || []
      });
    }
  }, [business]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const currentUserId = await getCurrentUserId();
      
      // Create a business data object without gallery_images
      const { gallery_images, ...businessData } = {
        ...formData,
        updated_at: new Date().toISOString(),
        status: formData.status || 'active',
        company_name: formData.company_name || formData.business_name,
        target_segment: formData.target_segment || '',
        website: formData.website || '',
        contact_email: formData.contact_email || ''
      };
      
      // If we're creating a new business, add the user_id
      if (!business) {
        businessData.user_id = currentUserId;
        businessData.created_at = businessData.updated_at;
      } else {
        // For existing businesses, add the ID but don't change the ownership
        businessData.id = business.id;
      }
      
      console.log('Saving business data:', businessData);
      
      // Save the business first
      const { data: savedBusiness, error: businessError } = await supabase
        .from('businesses')
        .upsert(businessData, {
          onConflict: 'id'
        })
        .select()
        .single();

      if (businessError) throw businessError;

      // Now handle gallery images if we have them
      if (formData.gallery_images?.length > 0) {
        const galleryData = formData.gallery_images.map((image, index) => ({
          business_id: savedBusiness.id,
          url: image.url,
          caption: image.caption,
          order_index: index
        }));

        // Delete existing gallery images for this business
        await supabase
          .from('business_gallery_images')
          .delete()
          .eq('business_id', savedBusiness.id);

        // Insert new gallery images
        const { error: galleryError } = await supabase
          .from('business_gallery_images')
          .insert(galleryData);

        if (galleryError) throw galleryError;
      }

      // Invalidate and refetch queries related to businesses
      queryClient.invalidateQueries({ queryKey: ['business-opportunities'] });
      
      toast.success(business ? 'Business updated successfully' : 'Business created successfully');
      onSuccess();
    } catch (error) {
      console.error('Error saving business:', error);
      toast.error(`Failed to ${business ? 'update' : 'create'} business: ${error.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <BusinessFormHeader business={business} />
      
      <div className="grid gap-6">
        {/* Primary Business Details */}
        <BusinessDetailsFields formData={formData} setFormData={setFormData} />
        
        {/* Categorization Fields */}
        <CategoryFields formData={formData} setFormData={setFormData} />
        
        {/* Contact Information */}
        <ContactInfoFields formData={formData} setFormData={setFormData} />
        
        {/* File Uploader */}
        <BusinessFileUploader 
          businessId={business?.id}
          logoUrl={formData.logo_url}
          pitchDeckUrl={formData.pitch_deck_url}
          websitePreviewUrl={formData.website_preview_url}
          galleryImages={formData.gallery_images}
          onLogoUpload={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
          onPitchDeckUpload={(url) => setFormData(prev => ({ ...prev, pitch_deck_url: url }))}
          onWebsitePreviewUpload={(url) => setFormData(prev => ({ ...prev, website_preview_url: url }))}
          onGalleryImagesChange={(images) => setFormData(prev => ({ ...prev, gallery_images: images }))}
        />
      </div>
      
      <FormActions 
        isSubmitting={isSubmitting} 
        onCancel={onClose} 
        isEditing={!!business} 
      />
    </form>
  );
};
